/**
 * @fileoverview Header bar shared by every dashboard-area page.
 * @description Renders once inside DashboardLayout, pinned above the routed page content (the
 * layout caps the page to the viewport height and only scrolls <main>, so this header never
 * scrolls out of view): the brand block, the language switcher (moved here from its old floating
 * corner position so it stays visible on every page), the notification bell/dropdown, and the
 * signed-in user's identity/logout dropdown. Below the `lg` breakpoint, a hamburger button appears
 * first (only when `onMenuClick` is passed) to toggle DashboardLayout's off-canvas nav drawer —
 * omitted above `lg`, where that sidebar is always visible and has nothing to toggle. Takes
 * `notifications` as a prop rather than reading the notifications feature itself, since shared/ may
 * not import a feature (ADR-0002) — DashboardShell (the app/ layer) resolves the list/mutations and
 * passes them down, the same boundary its own doc comment already calls out for
 * useFcmRegistration. The optional actions slot is unused by every current page (the source design
 * has one identical header everywhere) but keeps the component open to a future page that needs a
 * header-level action, instead of hardcoding "no per-page variation" into the shell.
 *
 * The brand block, the notification bell/dropdown, and the user identity/logout dropdown are each
 * their own component — `DashboardHeaderBrand`, `DashboardHeaderNotifications`, and
 * `DashboardHeaderUserMenu` — split out purely to keep this component's own render function short.
 * `DashboardHeaderBrand` is bank-aware: when `bank` is passed (a Banking-role user), it shows the
 * bank's own identity — a generic institution-glyph badge, the bank's name plus a "Banking" pill,
 * and the bank's own tagline — in place of the static InsightBox Sales name/tagline every
 * Sales-role user still sees. DashboardShell (the app/ layer) resolves `bank` from the signed-in
 * user's privileges and the banking dataset, since `shared/` may not import a feature (ADR-0002)
 * to fetch it itself.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import DashboardHeaderBrand from '@/shared/components/molecules/DashboardHeaderBrand'
import DashboardHeaderNotifications from '@/shared/components/molecules/DashboardHeaderNotifications'
import DashboardHeaderSearch from '@/shared/components/molecules/DashboardHeaderSearch'
import DashboardHeaderUserMenu from '@/shared/components/molecules/DashboardHeaderUserMenu'
import LanguageSwitcher from '@/shared/components/molecules/LanguageSwitcher'
import { SHOW_LANGUAGE_SWITCHER } from '@/shared/lib/i18n/language.constants'

import type { DashboardHeaderNotificationItem } from '@/shared/components/molecules/DashboardHeaderNotifications'
import type { AuthUser } from '@/shared/lib/auth/auth.types'
import type { ReactNode } from 'react'

export interface DashboardHeaderBank {
  name: string
  tagline: string
}

export interface DashboardHeaderNotificationsData {
  items: DashboardHeaderNotificationItem[]
  unreadCount: number
  onMarkAllRead: () => void
  onItemClick: (id: string) => void
}

interface DashboardHeaderProps {
  user: AuthUser | null
  privileges: string[]
  notifications: DashboardHeaderNotificationsData
  actions?: ReactNode
  onMenuClick?: () => void
  bank?: DashboardHeaderBank
  onOpenProfile?: () => void
  onOpenOrganisation?: () => void
}

/**
 * @component
 * @param {DashboardHeaderProps} props - The signed-in user, notification data, and an optional
 * header-level actions slot.
 * @param {AuthUser | null} props.user - The signed-in user, for the identity block/dropdown on the
 * right.
 * @param {string[]} props.privileges - Privilege keys from GET /identity/me, passed through to
 * DashboardHeaderSearch to build its search index.
 * @param {DashboardHeaderNotificationsData} props.notifications - The notification list, unread
 * count, and mark-read callbacks, passed straight through to DashboardHeaderNotifications.
 * @param {ReactNode} [props.actions] - Extra content rendered before the language switcher.
 * @param {() => void} [props.onMenuClick] - Toggles the off-canvas nav drawer; when omitted, no
 * hamburger button renders.
 * @param {DashboardHeaderBank} [props.bank] - The signed-in Banking-role user's bank identity;
 * renders in place of the static InsightBox Sales name/tagline when present.
 * @param {() => void} [props.onOpenProfile] - Opens the profile dialog from the user menu's
 * Profile Details item, passed straight through to DashboardHeaderUserMenu.
 * @param {() => void} [props.onOpenOrganisation] - Opens the organisation details dialog from the
 * user menu's Organisation Details item; the item shows only when this is passed.
 */
function DashboardHeader({
  user,
  privileges,
  notifications,
  actions,
  onMenuClick,
  bank,
  onOpenProfile,
  onOpenOrganisation,
}: DashboardHeaderProps) {
  const { t } = useTranslation()

  return (
    <header className="flex min-h-[66px] shrink-0 items-center gap-3 border-b border-divider bg-white px-3 py-2.5 sm:gap-4 sm:px-6">
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t('layout.header.toggleNav')}
          className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-label hover:bg-input-bg lg:hidden"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}
      <DashboardHeaderBrand {...(bank && { bank })} />

      {bank && (
        <div className="hidden flex-1 justify-center px-4 lg:flex">
          <DashboardHeaderSearch privileges={privileges} />
        </div>
      )}

      {actions}
      {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher />}
      <DashboardHeaderNotifications {...notifications} />

      {user && (
        <DashboardHeaderUserMenu
          user={user}
          {...(onOpenProfile && { onOpenProfile })}
          {...(onOpenOrganisation && { onOpenOrganisation })}
        />
      )}
    </header>
  )
}

export default DashboardHeader
